/**
 * Lapsed-subscriber content policy (backlog #63) — the pure decisions behind
 * every free "content door" that could otherwise serve a free user their
 * PAID-ERA history (bookmarks, incorrect answers, reviewed tests).
 *
 * Rule (decided 2026-09-19): with FREE_SET_ENFORCED on and the user free,
 *   - every LAUNCH draws only from the bounded free set (filterToFreeSet), and
 *   - every REVIEW / BOOKMARK surface still shows the user's record, but renders
 *     items outside the free set as LOCKED rows (number + domain + the user's
 *     correct/incorrect mark; no stem, choices, answer or explanation).
 * No grace window; a free-never-paid user with old history and a lapsed
 * subscriber get the same treatment. Premium users and flag-off brands pass
 * `freeSetIds = null` everywhere and nothing changes.
 *
 * THE READABLE SET (one definition, every door — `readableQuestionIds`): the
 * bounded free set PLUS every question the user legitimately met on a free
 * path — the questions of any FREE config test they took (the Assessment
 * draws by practice_test_id from the whole bank) and every Question of the
 * Day they answered — intersected with the bundle (orphan ids from retired
 * questions never count). A free-never-paid user therefore keeps everything
 * they could have reached without paying; a lapsed subscriber loses only what
 * they saw through premium tests, By Topic beyond the cap, or paid custom
 * tests. Custom tests are never "free config tests".
 *
 * Callers pass already-fetched plain objects; no SDKs, no UI.
 */

import { restrictToFreeSet } from './practiceFocus';

/** Which door produced a locked surface — analytics + copy scoping. */
export const LOCKED_DOORS = Object.freeze({
  BOOKMARKS_QUESTIONS: 'bookmarks_questions',
  TEST_REVIEW: 'test_review',
});

/**
 * Question ids a free user may read / practice (see the module header).
 * `null` when `freeSetIds` is null (premium / flag off / unresolved) — callers
 * then leave everything open. Order: free set first, then encountered ids.
 *
 * @param {Object} p
 * @param {Array|null} p.freeSetIds        bounded free set (filterToFreeSet ids)
 * @param {Array}      [p.questions]       bundled questions [{ id }] — anchors the set
 * @param {Array}      [p.practiceTestHistory] profile.practice_test_history
 * @param {Array}      [p.practiceTests]   GET_PRACTICE_TEST(exam) config rows
 * @param {Object}     [p.qotdResponses]   qotd.responses map ({ [date]: { question_id, … } })
 * @returns {Array|null}
 */
export function readableQuestionIds({ freeSetIds, questions, practiceTestHistory, practiceTests, qotdResponses } = {}) {
  if (!Array.isArray(freeSetIds)) return null;
  const seen = new Set();
  const out = [];
  const add = (id) => {
    if (id == null) return;
    const key = String(id);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(id);
  };
  freeSetIds.forEach(add);
  for (const entry of practiceTestHistory ?? []) {
    if (!isConfigTestFree(entry, practiceTests)) continue;
    for (const row of entry?.test_questions_meta_data ?? []) add(row?.question_id);
  }
  for (const r of Object.values(qotdResponses ?? {})) add(r?.question_id);
  if (!Array.isArray(questions)) return out;
  const bundle = new Set(questions.map((q) => String(q?.id)));
  return out.filter((id) => bundle.has(String(id)));
}

/**
 * Restrict EVERY custom-test pool to the free set. Replaces the per-app
 * "unanswered + attempted only" restriction (the retired Amendment 4 exempted
 * incorrect + bookmarked). Returns the same object when `freeSetIds` is null.
 *
 * @param {{ unanswered?: Array, attempted?: Array, incorrect?: Array, bookmarked?: Array }} pools
 *        question objects ({ id }) — see `getId` for score-record pools
 * @param {Array|null} freeSetIds
 * @param {(item: any) => any} [getId]
 */
export function applyFreeSetToPools(pools, freeSetIds, getId) {
  if (!Array.isArray(freeSetIds) || !pools) return pools;
  const out = { ...pools };
  for (const key of ['unanswered', 'attempted', 'incorrect', 'bookmarked']) {
    if (Array.isArray(pools[key])) out[key] = restrictToFreeSet(pools[key], freeSetIds, getId);
  }
  return out;
}

/**
 * Is a stored history entry a brand-config test that is free for everyone?
 * Custom tests (`is_custom_test`, or a test_name the config doesn't know) are
 * never free config tests — their content came from the user's own selection.
 *
 * @param {{ test_name?: string, is_custom_test?: boolean }} historyEntry
 * @param {Array<{ test_name: string, requires_subscription?: boolean }>} practiceTests GET_PRACTICE_TEST(exam)
 * @returns {boolean}
 */
export function isConfigTestFree(historyEntry, practiceTests) {
  if (!historyEntry || historyEntry.is_custom_test === true) return false;
  const cfg = (practiceTests ?? []).find((t) => t?.test_name === historyEntry.test_name);
  if (!cfg) return false;
  return cfg.requires_subscription !== true;
}

/**
 * Is a config test premium (blocked whole for free users — existing mobile
 * behaviour, now shared so web gets the same gate)? A custom test is NOT
 * premium as a whole; it is partitioned per question instead.
 */
export function isConfigTestPremium(historyEntry, practiceTests) {
  if (!historyEntry || historyEntry.is_custom_test === true) return false;
  const cfg = (practiceTests ?? []).find((t) => t?.test_name === historyEntry.test_name);
  return cfg?.requires_subscription === true;
}

/**
 * Annotate reviewed test rows (`test_questions_meta_data` entries, keyed by
 * `question_id`) with `locked`. Order and length are preserved so the review
 * pager's indices and the Correct/Incorrect/domain sub-lists stay consistent.
 * A free config test's own rows are readable by construction (its ids are in
 * the readable set), so there is no per-test exception here.
 *
 * @param {Array<{ question_id: any }>} rows
 * @param {{ readableIds: Array|null }} ctx  readableQuestionIds output
 * @returns {{ rows: Array, lockedCount: number }}
 */
export function partitionReviewRows(rows, { readableIds } = {}) {
  const list = Array.isArray(rows) ? rows : [];
  if (!Array.isArray(readableIds)) {
    return { rows: list.map((r) => ({ ...r, locked: false })), lockedCount: 0 };
  }
  const readable = new Set(readableIds.map((id) => String(id)));
  let lockedCount = 0;
  const annotated = list.map((r) => {
    const locked = !readable.has(String(r?.question_id));
    if (locked) lockedCount += 1;
    return { ...r, locked };
  });
  return { rows: annotated, lockedCount };
}

/**
 * Annotate bookmarked questions (`{ id }`) with `locked` against the readable
 * set. Order preserved.
 *
 * @param {Array<{ id: any }>} items
 * @param {Array|null} readableIds
 * @returns {{ items: Array, lockedCount: number }}
 */
export function partitionBookmarks(items, readableIds) {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  if (!Array.isArray(readableIds)) {
    return { items: list.map((i) => ({ ...i, locked: false })), lockedCount: 0 };
  }
  const readable = new Set(readableIds.map((id) => String(id)));
  let lockedCount = 0;
  const annotated = list.map((i) => {
    const locked = !readable.has(String(i.id));
    if (locked) lockedCount += 1;
    return { ...i, locked };
  });
  return { items: annotated, lockedCount };
}

/**
 * The launchable subset of a review / bookmark list for a free user (retake,
 * "practice bookmarks"). Empty result = the caller shows the paywall instead of
 * starting an empty session. `getId` adapts meta rows (`question_id`).
 * Pass the READABLE ids for questions; flashcards / articles pass their own
 * free set.
 */
export function restrictLaunch(items, readableIds, getId) {
  return restrictToFreeSet(items ?? [], readableIds, getId);
}

/**
 * Does this user own history outside the readable set (paid-era answers or
 * bookmarks)? Drives the upgrade sentence in the exhaustion modal. Bundle-
 * anchored: score records / bookmarks for ids not in `questions` (retired or
 * renumbered content) are ignored, as computeQuestionPools does.
 *
 * @param {{ scores?: Array<{question_id:any, times_answered?:number}>, bookmarks?: Array,
 *           readableIds: Array|null, questions?: Array<{id:any}> }} p
 */
export function hasHistoryOutsideFreeSet({ scores, bookmarks, readableIds, questions } = {}) {
  if (!Array.isArray(readableIds)) return false;
  const readable = new Set(readableIds.map((id) => String(id)));
  const bundle = Array.isArray(questions) ? new Set(questions.map((q) => String(q?.id))) : null;
  const inBundle = (id) => !bundle || bundle.has(String(id));
  const outside = (id) => id != null && inBundle(id) && !readable.has(String(id));
  const answered = (scores ?? []).some((s) => s && (s.times_answered ?? 0) >= 1 && outside(s.question_id));
  if (answered) return true;
  return (bookmarks ?? []).some(outside);
}

/**
 * Copy for locked surfaces — one source for web + mobile. Wording is tier-
 * neutral ("upgrade"): a never-paid user with old history reads it too.
 * @param {{ door: string, lockedCount: number }} p
 */
export function lockedCopy({ door, lockedCount } = {}) {
  const noun = door === LOCKED_DOORS.TEST_REVIEW ? 'question' : 'saved question';
  const n = Number(lockedCount) || 0;
  const plural = n === 1 ? noun : `${noun}s`;
  return {
    banner: `${n} ${plural} ${n === 1 ? 'is' : 'are'} locked on the free plan. Upgrade to review ${n === 1 ? 'it' : 'them'}.`,
    rowLabel: 'Locked',
    rowHint: 'Upgrade to view',
    emptyLaunchTitle: 'Your saved items are locked on the free plan',
    historyWaiting: 'Your saved and missed questions are waiting when you upgrade.',
    unlock: 'Upgrade',
  };
}

/**
 * The footer CTA on the LAST question of a free session — the moment the free
 * user has just run out. Count-based, never a bare "Subscribe":
 *   domain    → "Unlock 224 more in this domain"   (By Topic free slice)
 *   bookmarks → "Unlock your 2 locked bookmarks"    (bookmark session, #63)
 * Returns null when there is nothing to unlock, so the caller shows nothing
 * (or its legacy button). Paywall `source` lets PostHog compare the two doors.
 *
 * @param {{ scope: 'domain'|'bookmarks', lockedCount: number }} [p]
 * @returns {{ label: string, source: string }|null}
 */
export function lastFreeUpsell({ scope, lockedCount } = {}) {
  const n = Number(lockedCount) || 0;
  if (n <= 0) return null;
  if (scope === 'bookmarks') {
    return { label: `Unlock your ${n} locked ${n === 1 ? 'bookmark' : 'bookmarks'}`, source: 'bookmarks_last_open' };
  }
  if (scope === 'domain') {
    return { label: `Unlock ${n} more in this domain`, source: 'study_last_free_question' };
  }
  return null;
}

/**
 * The summary dialog shown before any paywall hop out of Test Review (#63):
 * say the split once, up front, instead of paging past locked cards or
 * jumping to the paywall with no explanation.
 *   - mixed custom test          → "Of the 25, 1 is available… Upgrade to unlock the remaining 24."
 *   - nothing open               → "All 5 questions in this test are locked…"
 *   - premium config test        → "Mock Exam 1 is a premium test. Upgrade to review all 25 questions."
 *   - action 'retake'            → same, worded for retaking
 * `reviewFree` is null when nothing is open (only Upgrade / close).
 *
 * @param {{ openCount?: number, lockedCount?: number, premiumTest?: boolean, testName?: string,
 *           action?: 'review'|'retake' }} p
 * @returns {{ title: string, message: string, reviewFree: string|null, upgrade: string }}
 */
export function lockedReviewDialogCopy({ openCount, lockedCount, premiumTest = false, testName, action = 'review' } = {}) {
  const open = Number(openCount) || 0;
  const locked = Number(lockedCount) || 0;
  const total = open + locked;
  const q = (n) => (n === 1 ? 'question' : 'questions');
  const verb = action === 'retake' ? 'retake' : 'review';
  if (premiumTest) {
    return {
      title: 'This test is premium',
      message: `${testName || 'This test'} is a premium test. Upgrade to ${verb} ${total > 0 ? `all ${total} ${q(total)}` : 'it'}.`,
      reviewFree: null,
      upgrade: 'Upgrade to unlock',
    };
  }
  return {
    title: open === 0 ? `This ${action === 'retake' ? 'retake' : 'review'} is locked on the free plan` : `Part of this ${action === 'retake' ? 'test' : 'review'} is locked`,
    message:
      open === 0
        ? `All ${total} ${q(total)} in this test are locked on the free plan. Upgrade to ${verb} them.`
        : `Of the ${total} ${q(total)}, ${open} ${open === 1 ? 'is' : 'are'} available to ${verb} on the free plan. Upgrade to unlock the remaining ${locked}.`,
    reviewFree: open === 0 ? null : `${action === 'retake' ? 'Retake' : 'Review'} the ${open} free ${q(open)}`,
    upgrade: 'Upgrade to unlock',
  };
}
